import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNewNG } from '../context/NewNGContext';
import { newNGApi } from '../services/newNGApi';
import Typography from '../../../basic/Typography';
import { TheatreMode } from './TheatreMode';
import { NewNGJob } from '../types';
import styles from '../NewNG.module.css';

const DEFAULT_PLAYLIST_SIZE = 5;
const BACKGROUND_REFRESH_INTERVAL_MS = 60000;
const MAX_RETRY_DELAY_MS = 15000;
const INITIAL_RETRY_DELAY_MS = 1000;

interface TheatrePageProps {
  initialJobId?: string;
  playlistSize?: number;
}

export const TheatrePage: React.FC<TheatrePageProps> = ({
  initialJobId,
  playlistSize = DEFAULT_PLAYLIST_SIZE
}) => {
  const { state, dispatch } = useNewNG();
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);
  const [playlist, setPlaylist] = useState<NewNGJob[]>([]);
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
  const [playlistHeadJobId, setPlaylistHeadJobId] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundRefreshRef = useRef<NodeJS.Timeout | null>(null);

  const calculateRetryDelay = useCallback((attemptNumber: number): number => {
    const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attemptNumber);
    return Math.min(delay, MAX_RETRY_DELAY_MS);
  }, []);

  const getLatestCompleteJobs = useCallback((jobs: NewNGJob[], count: number): NewNGJob[] => {
    const completeJobs = jobs.filter(job =>
      job.overall_status === 'completed' &&
      job.stages?.composition?.completed === true
    );

    return completeJobs
      .filter(job => job.created_at)
      .sort((a, b) => {
        const dateA = new Date(a.created_at || '').getTime();
        const dateB = new Date(b.created_at || '').getTime();
        return dateB - dateA;
      })
      .slice(0, count);
  }, []);

  const hasPlaylistChanged = useCallback((newJobs: NewNGJob[]): boolean => {
    if (newJobs.length === 0) {
      return false;
    }
    if (!playlistHeadJobId) {
      return true;
    }
    return newJobs[0]?.job_id !== playlistHeadJobId;
  }, [playlistHeadJobId]);

  const refreshPlaylist = useCallback(async (isRetryAttempt = false) => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    try {
      if (!isRetryAttempt) {
        dispatch({ type: 'SET_LOADING', payload: true });
      }

      const jobs = await newNGApi.getJobs();
      dispatch({ type: 'SET_JOBS', payload: jobs });

      const latestJobs = getLatestCompleteJobs(jobs, playlistSize);

      if (latestJobs.length === 0) {
        console.warn('No complete jobs available for playlist - will retry');
        const warningMessage = `No videos available yet. Retrying in ${Math.ceil(calculateRetryDelay(retryCount) / 1000)}s...`;
        dispatch({ type: 'SET_ERROR', payload: warningMessage });

        const delay = calculateRetryDelay(retryCount);
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          refreshPlaylist(true);
        }, delay);
        return;
      }

      setRetryCount(0);

      if (hasPlaylistChanged(latestJobs)) {
        console.log('Playlist updated - new videos available, restarting from latest');
        setPlaylist(latestJobs);
        setPlaylistHeadJobId(latestJobs[0].job_id);
        setCurrentPlaylistIndex(0);
        dispatch({ type: 'SET_CURRENT_JOB', payload: latestJobs[0] });
      } else {
        console.log('Playlist unchanged - continuing current sequence');
      }

      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      console.error('Failed to load playlist:', error);
      const delay = calculateRetryDelay(retryCount);
      const retryMessage = `Failed to load videos. Retrying in ${Math.ceil(delay / 1000)}s...`;
      dispatch({ type: 'SET_ERROR', payload: retryMessage });

      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        refreshPlaylist(true);
      }, delay);
    } finally {
      if (!isRetryAttempt) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [dispatch, getLatestCompleteJobs, hasPlaylistChanged, playlistSize, retryCount, calculateRetryDelay]);

  useEffect(() => {
    refreshPlaylist();

    backgroundRefreshRef.current = setInterval(() => {
      console.log('Background refresh: Checking for new videos...');
      refreshPlaylist(false);
    }, BACKGROUND_REFRESH_INTERVAL_MS);

    return () => {
      if (backgroundRefreshRef.current) {
        clearInterval(backgroundRefreshRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [refreshPlaylist]);

  const handleVideoEnd = useCallback(async () => {
    console.log('Video ended, advancing to next video...');

    try {
      await refreshPlaylist(false);
    } catch (error) {
      console.warn('Refresh failed at video end, continuing with existing playlist:', error);
    }

    if (playlist.length === 0) {
      console.warn('No videos in playlist, will retry on next refresh cycle');
      return;
    }

    const nextIndex = currentPlaylistIndex + 1;
    const hasMoreVideos = nextIndex < playlist.length;

    if (hasMoreVideos) {
      console.log(`Playing video ${nextIndex + 1} of ${playlist.length}`);
      setCurrentPlaylistIndex(nextIndex);
      dispatch({ type: 'SET_CURRENT_JOB', payload: playlist[nextIndex] });
      return;
    }

    console.log('End of playlist - looping back to start');
    setCurrentPlaylistIndex(0);
    dispatch({ type: 'SET_CURRENT_JOB', payload: playlist[0] });
  }, [currentPlaylistIndex, playlist, refreshPlaylist, dispatch]);

  const handleJobSelect = useCallback((job: NewNGJob) => {
    dispatch({ type: 'SET_CURRENT_JOB', payload: job });
    dispatch({ type: 'SET_ERROR', payload: null });
    setIsErrorDismissed(false);
  }, [dispatch]);

  // Handle error message dismissal
  const handleErrorDismiss = useCallback(() => {
    setIsErrorDismissed(true);
    // Auto-hide after animation
    setTimeout(() => {
      dispatch({ type: 'SET_ERROR', payload: null });
    }, 300);
  }, [dispatch]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      // Note: NewNG doesn't have fullscreen state, but we can add it if needed
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className={styles['newng-theatre-page']}>
      {/* Play button overlay for autoplay prevented */}
      {state.error && state.error.includes('Autoplay prevented') && !isErrorDismissed && (
        <div className={styles['newng-theatre-play-button-overlay']} onClick={handleErrorDismiss}>
          <div className={styles['newng-theatre-play-button']}>
            <svg viewBox="0 0 24 24" fill="currentColor" className={styles['newng-theatre-play-icon']}>
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      )}

      {/* Error display for other errors */}
      {state.error && !state.error.includes('Autoplay prevented') && !isErrorDismissed && (
        <div className={`${styles['newng-theatre-error-message']} ${isErrorDismissed ? styles['fade-out'] : ''}`} onClick={handleErrorDismiss}>
          <Typography variant="body2" gutterBottom={false} style={{ fontWeight: '600' }}>
            ⚠️ Attention
          </Typography>
          <Typography variant="body2" gutterBottom={false} style={{ marginTop: '0.3rem', opacity: 0.9 }}>
            {state.error}
          </Typography>
        </div>
      )}

      {/* Main video broadcast */}
      <TheatreMode
        jobId={state.currentJob?.job_id || ''}
        networkName="FEI"
        jobs={state.jobs}
        currentJob={state.currentJob}
        onJobChange={(jobId) => {
          const job = state.jobs.find(j => j.job_id === jobId);
          if (job) {
            handleJobSelect(job);
          }
        }}
        onVideoEnd={handleVideoEnd}
        isJobsLoading={state.isLoading}
        onClose={() => window.location.href = '/game/newsng'}
      />
    </div>
  );
};
